import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const FormTemplates = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1: Name, 2: Specialty, 3: Disease, 4: Generated Form
  const [useManualEntry, setUseManualEntry] = useState(false);
  
  // Add management-related states
  const [showInactive, setShowInactive] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Add field editing states for preview mode
  const [editingField, setEditingField] = useState(null);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [newField, setNewField] = useState({
    label: '',
    type: 'text',
    required: false,
    placeholder: '',
    options: []
  });
  
  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    disease: '',
    template_data: {
      sections: []
    }
  });

  // Add smart template generation states
  const [availableSpecialties, setAvailableSpecialties] = useState([]);
  const [specialtyDetails, setSpecialtyDetails] = useState({});
  const [smartTemplatePreview, setSmartTemplatePreview] = useState(null);
  const [smartTemplateLoading, setSmartTemplateLoading] = useState(false);
  const [generationMethod, setGenerationMethod] = useState('smart'); // 'smart' or 'manual'

  const specialties = [
    'All Specialties',
    'Cardiology',
    'Endocrinology',
    'Neurology',
    'Oncology',
    'Psychiatry',
    'Orthopedics',
    'Pediatrics',
    'Geriatrics',
    'Emergency Medicine',
    'Internal Medicine'
  ];

  // Disease categories by specialty
  const diseasesBySpecialty = {
    'Cardiology': [
      'Coronary Artery Disease',
      'Heart Failure',
      'Atrial Fibrillation',
      'Hypertension',
      'Myocardial Infarction',
      'Cardiomyopathy',
      'Valvular Heart Disease',
      'Arrhythmias'
    ],
    'Endocrinology': [
      'Type 1 Diabetes',
      'Type 2 Diabetes',
      'Hypothyroidism',
      'Hyperthyroidism',
      'Metabolic Syndrome',
      'Osteoporosis',
      'Adrenal Disorders',
      'PCOS'
    ],
    'Neurology': [
      'Stroke',
      'Epilepsy',
      'Multiple Sclerosis',
      'Parkinson\'s Disease',
      'Alzheimer\'s Disease',
      'Migraine',
      'Neuropathy',
      'Brain Tumors'
    ],
    'Oncology': [
      'Breast Cancer',
      'Lung Cancer',
      'Colorectal Cancer',
      'Prostate Cancer',
      'Lymphoma',
      'Leukemia',
      'Melanoma',
      'Ovarian Cancer'
    ],
    'Psychiatry': [
      'Depression',
      'Anxiety Disorders',
      'Bipolar Disorder',
      'Schizophrenia',
      'PTSD',
      'ADHD',
      'Eating Disorders',
      'Substance Abuse'
    ],
    'Orthopedics': [
      'Osteoarthritis',
      'Rheumatoid Arthritis',
      'Fractures',
      'Spinal Disorders',
      'Sports Injuries',
      'Joint Replacement',
      'Bone Tumors',
      'Osteoporosis'
    ],
    'Pediatrics': [
      'Asthma',
      'Allergies',
      'Growth Disorders',
      'Developmental Delays',
      'Infections',
      'Congenital Disorders',
      'Behavioral Issues',
      'Vaccination'
    ],
    'Geriatrics': [
      'Dementia',
      'Falls',
      'Polypharmacy',
      'Frailty',
      'Incontinence',
      'Osteoporosis',
      'Depression',
      'Chronic Pain'
    ]
  };

  // Form generation logic based on specialty and disease
  const generateFormTemplate = (specialty, disease) => {
    // Remove Patient ID field as it's managed at MRS level, not template level
    const baseFields = [
      {
        title: 'Patient Demographics',
        fields: [
          { name: 'age', type: 'number', label: 'Age (years)', required: true, min: 0, max: 120 },
          { name: 'gender', type: 'select', label: 'Gender', options: ['Male', 'Female', 'Other'], required: true },
          { name: 'weight', type: 'number', label: 'Weight (kg)', min: 0, max: 300 },
          { name: 'height', type: 'number', label: 'Height (cm)', min: 50, max: 250 },
          { name: 'bmi', type: 'number', label: 'BMI', readonly: true }
        ]
      }
    ];

    // Specialty and disease-specific sections
    const specialtySpecificSections = getSpecialtySpecificSections(specialty, disease);
    
    return {
      sections: [...baseFields, ...specialtySpecificSections]
    };
  };

  const getSpecialtySpecificSections = (specialty, disease) => {
    const sections = [];

    // Common medical history section
    sections.push({
      title: 'Medical History',
      fields: [
        { name: 'chief_complaint', type: 'textarea', label: 'Chief Complaint', rows: 3 },
        { name: 'history_present_illness', type: 'textarea', label: 'History of Present Illness', rows: 4 },
        { name: 'past_medical_history', type: 'textarea', label: 'Past Medical History', rows: 3 },
        { name: 'medications', type: 'textarea', label: 'Current Medications', rows: 3 },
        { name: 'allergies', type: 'text', label: 'Known Allergies' },
        { name: 'family_history', type: 'textarea', label: 'Family History', rows: 2 }
      ]
    });

    // Check if specialty has predefined logic, otherwise use general approach
    const predefinedSpecialties = ['Cardiology', 'Endocrinology', 'Neurology', 'Oncology', 'Psychiatry', 'Orthopedics', 'Pediatrics', 'Geriatrics'];
    
    if (predefinedSpecialties.includes(specialty)) {
      // Use existing specialty-specific logic
      switch (specialty) {
        case 'Cardiology':
          sections.push(getCardiologySection(disease));
          break;
        case 'Endocrinology':
          sections.push(getEndocrinologySection(disease));
          break;
        case 'Neurology':
          sections.push(getNeurologySection(disease));
          break;
        case 'Oncology':
          sections.push(getOncologySection(disease));
          break;
        case 'Psychiatry':
          sections.push(getPsychiatrySection(disease));
          break;
        case 'Orthopedics':
          sections.push(getOrthopedicsSection(disease));
          break;
        case 'Pediatrics':
          sections.push(getPediatricsSection(disease));
          break;
        case 'Geriatrics':
          sections.push(getGeriatricsSection(disease));
          break;
      }
    } else {
      // Generate custom section for non-predefined specialties
      sections.push(generateCustomSpecialtySection(specialty, disease));
    }

    // Common outcomes section
    sections.push({
      title: 'Treatment & Outcomes',
      fields: [
        { name: 'treatment_plan', type: 'textarea', label: 'Treatment Plan', rows: 4 },
        { name: 'prescribed_medications', type: 'textarea', label: 'Prescribed Medications', rows: 3 },
        { name: 'primary_outcome', type: 'text', label: 'Primary Outcome' },
        { name: 'secondary_outcomes', type: 'textarea', label: 'Secondary Outcomes', rows: 2 },
        { name: 'adverse_events', type: 'textarea', label: 'Adverse Events', rows: 2 },
        { name: 'follow_up_plan', type: 'textarea', label: 'Follow-up Plan', rows: 2 },
        { name: 'efficacy_rating', type: 'select', label: 'Treatment Efficacy', options: ['Excellent', 'Good', 'Fair', 'Poor', 'Not Assessed'] }
      ]
    });

    return sections;
  };

  const generateCustomSpecialtySection = (specialty, disease) => {
    // Generate a custom section based on specialty and disease
    const sectionTitle = `${specialty} Assessment`;
    
    const baseFields = [
      { name: 'clinical_presentation', type: 'textarea', label: 'Clinical Presentation', rows: 3 },
      { name: 'physical_examination', type: 'textarea', label: 'Physical Examination Findings', rows: 4 },
      { name: 'diagnostic_tests', type: 'textarea', label: 'Diagnostic Tests & Results', rows: 3 },
      { name: 'severity_assessment', type: 'select', label: 'Condition Severity', options: ['Mild', 'Moderate', 'Severe', 'Critical'] },
      { name: 'risk_factors', type: 'textarea', label: 'Risk Factors', rows: 2 },
      { name: 'complications', type: 'textarea', label: 'Complications (if any)', rows: 2 }
    ];

    // Add disease-specific fields based on common medical patterns
    const diseaseSpecificFields = generateDiseaseSpecificFields(disease);

    return {
      title: sectionTitle,
      fields: [...baseFields, ...diseaseSpecificFields]
    };
  };

  const generateDiseaseSpecificFields = (disease) => {
    const fields = [];
    const diseaseLower = disease.toLowerCase();

    // Add fields based on disease patterns
    if (diseaseLower.includes('diabetes') || diseaseLower.includes('glucose')) {
      fields.push(
        { name: 'blood_glucose', type: 'number', label: 'Blood Glucose Level (mg/dL)', min: 50, max: 500 },
        { name: 'hba1c', type: 'number', label: 'HbA1c (%)', min: 4, max: 15, step: 0.1 }
      );
    }

    if (diseaseLower.includes('hypertension') || diseaseLower.includes('blood pressure')) {
      fields.push(
        { name: 'systolic_bp', type: 'number', label: 'Systolic BP (mmHg)', min: 80, max: 250 },
        { name: 'diastolic_bp', type: 'number', label: 'Diastolic BP (mmHg)', min: 40, max: 150 }
      );
    }

    if (diseaseLower.includes('pain') || diseaseLower.includes('arthritis')) {
      fields.push(
        { name: 'pain_scale', type: 'number', label: 'Pain Scale (0-10)', min: 0, max: 10 },
        { name: 'pain_location', type: 'text', label: 'Pain Location' },
        { name: 'pain_duration', type: 'text', label: 'Pain Duration' }
      );
    }

    if (diseaseLower.includes('infection') || diseaseLower.includes('fever')) {
      fields.push(
        { name: 'temperature', type: 'number', label: 'Temperature (°F)', min: 95, max: 110, step: 0.1 },
        { name: 'white_blood_cell_count', type: 'number', label: 'WBC Count (cells/μL)', min: 1000, max: 50000 }
      );
    }

    if (diseaseLower.includes('cancer') || diseaseLower.includes('tumor')) {
      fields.push(
        { name: 'tumor_size', type: 'text', label: 'Tumor Size (cm)' },
        { name: 'metastasis', type: 'select', label: 'Metastasis', options: ['None', 'Local', 'Regional', 'Distant'] },
        { name: 'biopsy_results', type: 'textarea', label: 'Biopsy Results', rows: 3 }
      );
    }

    if (diseaseLower.includes('heart') || diseaseLower.includes('cardiac')) {
      fields.push(
        { name: 'heart_rate', type: 'number', label: 'Heart Rate (bpm)', min: 30, max: 200 },
        { name: 'ecg_findings', type: 'textarea', label: 'ECG Findings', rows: 2 }
      );
    }

    if (diseaseLower.includes('lung') || diseaseLower.includes('respiratory') || diseaseLower.includes('asthma')) {
      fields.push(
        { name: 'respiratory_rate', type: 'number', label: 'Respiratory Rate (breaths/min)', min: 8, max: 60 },
        { name: 'oxygen_saturation', type: 'number', label: 'Oxygen Saturation (%)', min: 70, max: 100 }
      );
    }

    if (diseaseLower.includes('kidney') || diseaseLower.includes('renal')) {
      fields.push(
        { name: 'creatinine', type: 'number', label: 'Creatinine (mg/dL)', min: 0.5, max: 10, step: 0.1 },
        { name: 'gfr', type: 'number', label: 'GFR (mL/min/1.73m²)', min: 5, max: 120 }
      );
    }

    if (diseaseLower.includes('liver') || diseaseLower.includes('hepatic')) {
      fields.push(
        { name: 'alt', type: 'number', label: 'ALT (U/L)', min: 5, max: 1000 },
        { name: 'ast', type: 'number', label: 'AST (U/L)', min: 5, max: 1000 },
        { name: 'bilirubin', type: 'number', label: 'Total Bilirubin (mg/dL)', min: 0.1, max: 20, step: 0.1 }
      );
    }

    // Add generic fields if no specific patterns found
    if (fields.length === 0) {
      fields.push(
        { name: 'laboratory_results', type: 'textarea', label: 'Laboratory Results', rows: 3 },
        { name: 'imaging_findings', type: 'textarea', label: 'Imaging Findings', rows: 3 },
        { name: 'specialist_consultation', type: 'textarea', label: 'Specialist Consultation Notes', rows: 2 }
      );
    }

    return fields;
  };

  const getCardiologySection = (disease) => {
    const baseCardiology = {
      title: 'Cardiovascular Assessment',
      fields: [
        { name: 'chest_pain', type: 'select', label: 'Chest Pain', options: ['None', 'Typical Angina', 'Atypical Angina', 'Non-cardiac'] },
        { name: 'dyspnea', type: 'select', label: 'Dyspnea (NYHA Class)', options: ['None', 'Class I', 'Class II', 'Class III', 'Class IV'] },
        { name: 'palpitations', type: 'checkbox', label: 'Palpitations' },
        { name: 'syncope', type: 'checkbox', label: 'Syncope/Pre-syncope' },
        { name: 'blood_pressure', type: 'text', label: 'Blood Pressure (mmHg)', placeholder: '120/80' },
        { name: 'heart_rate', type: 'number', label: 'Heart Rate (bpm)', min: 30, max: 200 },
        { name: 'cardiac_risk_factors', type: 'multiselect', label: 'Cardiac Risk Factors', options: ['Hypertension', 'Diabetes', 'Smoking', 'Family History CAD', 'Hyperlipidemia', 'Obesity'] }
      ]
    };

    // Disease-specific additions
    switch (disease) {
      case 'Coronary Artery Disease':
        baseCardiology.fields.push(
          { name: 'stress_test_result', type: 'select', label: 'Stress Test Result', options: ['Not Done', 'Normal', 'Abnormal', 'Indeterminate'] },
          { name: 'cardiac_catheterization', type: 'textarea', label: 'Cardiac Catheterization Results', rows: 3 },
          { name: 'vessel_involvement', type: 'multiselect', label: 'Vessel Involvement', options: ['LAD', 'RCA', 'LCX', 'LMCA', 'Diagonal', 'Marginal'] }
        );
        break;
      case 'Heart Failure':
        baseCardiology.fields.push(
          { name: 'ejection_fraction', type: 'number', label: 'Ejection Fraction (%)', min: 10, max: 80 },
          { name: 'hf_type', type: 'select', label: 'Heart Failure Type', options: ['HFrEF', 'HFpEF', 'HFmrEF'] },
          { name: 'bnp_levels', type: 'number', label: 'BNP/NT-proBNP (pg/mL)' }
        );
        break;
      case 'Atrial Fibrillation':
        baseCardiology.fields.push(
          { name: 'af_type', type: 'select', label: 'AF Type', options: ['Paroxysmal', 'Persistent', 'Long-standing Persistent', 'Permanent'] },
          { name: 'chads2_vasc_score', type: 'number', label: 'CHA2DS2-VASc Score', min: 0, max: 9 },
          { name: 'anticoagulation', type: 'select', label: 'Anticoagulation', options: ['None', 'Warfarin', 'Dabigatran', 'Rivaroxaban', 'Apixaban', 'Edoxaban'] }
        );
        break;
    }

    return baseCardiology;
  };

  const getEndocrinologySection = (disease) => {
    const baseEndocrinology = {
      title: 'Endocrine Assessment',
      fields: [
        { name: 'symptoms', type: 'multiselect', label: 'Endocrine Symptoms', options: ['Fatigue', 'Weight Changes', 'Temperature Intolerance', 'Mood Changes', 'Sleep Disturbances', 'Appetite Changes'] },
        { name: 'vital_signs', type: 'text', label: 'Vital Signs', placeholder: 'BP, HR, Temp, RR' }
      ]
    };

    switch (disease) {
      case 'Type 1 Diabetes':
      case 'Type 2 Diabetes':
        baseEndocrinology.fields.push(
          { name: 'diabetes_duration', type: 'number', label: 'Duration of Diabetes (years)', min: 0 },
          { name: 'hba1c', type: 'number', label: 'HbA1c (%)', min: 4, max: 15, step: 0.1 },
          { name: 'fasting_glucose', type: 'number', label: 'Fasting Glucose (mg/dL)', min: 50, max: 500 },
          { name: 'diabetic_complications', type: 'multiselect', label: 'Diabetic Complications', options: ['Retinopathy', 'Nephropathy', 'Neuropathy', 'Foot Ulcers', 'CAD', 'Stroke'] },
          { name: 'insulin_regimen', type: 'textarea', label: 'Insulin/Medication Regimen', rows: 3 }
        );
        break;
      case 'Hypothyroidism':
      case 'Hyperthyroidism':
        baseEndocrinology.fields.push(
          { name: 'tsh_level', type: 'number', label: 'TSH Level (mIU/L)', step: 0.01 },
          { name: 'free_t4', type: 'number', label: 'Free T4 (ng/dL)', step: 0.1 },
          { name: 'thyroid_antibodies', type: 'text', label: 'Thyroid Antibodies' },
          { name: 'thyroid_symptoms', type: 'multiselect', label: 'Thyroid Symptoms', options: ['Hair Loss', 'Weight Changes', 'Heart Palpitations', 'Tremor', 'Heat/Cold Intolerance', 'Constipation', 'Diarrhea'] }
        );
        break;
    }

    return baseEndocrinology;
  };

  const getNeurologySection = (disease) => {
    const baseNeurology = {
      title: 'Neurological Assessment',
      fields: [
        { name: 'neurological_symptoms', type: 'multiselect', label: 'Neurological Symptoms', options: ['Headache', 'Dizziness', 'Weakness', 'Numbness', 'Speech Problems', 'Vision Changes', 'Memory Issues', 'Seizures'] },
        { name: 'mental_status', type: 'select', label: 'Mental Status', options: ['Alert', 'Confused', 'Lethargic', 'Obtunded', 'Stuporous', 'Comatose'] },
        { name: 'motor_function', type: 'textarea', label: 'Motor Function Assessment', rows: 2 },
        { name: 'sensory_function', type: 'textarea', label: 'Sensory Function Assessment', rows: 2 }
      ]
    };

    switch (disease) {
      case 'Stroke':
        baseNeurology.fields.push(
          { name: 'stroke_type', type: 'select', label: 'Stroke Type', options: ['Ischemic', 'Hemorrhagic', 'TIA'] },
          { name: 'nihss_score', type: 'number', label: 'NIHSS Score', min: 0, max: 42 },
          { name: 'onset_time', type: 'datetime-local', label: 'Symptom Onset Time' },
          { name: 'thrombolysis', type: 'checkbox', label: 'Thrombolysis Given' }
        );
        break;
      case 'Epilepsy':
        baseNeurology.fields.push(
          { name: 'seizure_type', type: 'select', label: 'Seizure Type', options: ['Focal', 'Generalized', 'Unknown Onset'] },
          { name: 'seizure_frequency', type: 'text', label: 'Seizure Frequency' },
          { name: 'aed_medications', type: 'textarea', label: 'Anti-Epileptic Drugs', rows: 2 },
          { name: 'eeg_findings', type: 'textarea', label: 'EEG Findings', rows: 2 }
        );
        break;
    }

    return baseNeurology;
  };

  const getOncologySection = (disease) => {
    return {
      title: 'Oncology Assessment',
      fields: [
        { name: 'cancer_stage', type: 'select', label: 'Cancer Stage', options: ['Stage 0', 'Stage I', 'Stage II', 'Stage III', 'Stage IV', 'Unknown'] },
        { name: 'histology', type: 'text', label: 'Histological Type' },
        { name: 'grade', type: 'select', label: 'Grade', options: ['G1 - Well differentiated', 'G2 - Moderately differentiated', 'G3 - Poorly differentiated', 'G4 - Undifferentiated'] },
        { name: 'performance_status', type: 'select', label: 'ECOG Performance Status', options: ['0 - Fully active', '1 - Restricted in strenuous activity', '2 - Ambulatory >50% of time', '3 - Confined to bed/chair >50% of time', '4 - Completely disabled'] },
        { name: 'tumor_markers', type: 'textarea', label: 'Tumor Markers', rows: 2 },
        { name: 'treatment_regimen', type: 'textarea', label: 'Treatment Regimen', rows: 3 },
        { name: 'response_assessment', type: 'select', label: 'Response to Treatment', options: ['Complete Response', 'Partial Response', 'Stable Disease', 'Progressive Disease', 'Not Assessed'] }
      ]
    };
  };

  const getPsychiatrySection = (disease) => {
    const basePsychiatry = {
      title: 'Psychiatric Assessment',
      fields: [
        { name: 'mental_state_exam', type: 'textarea', label: 'Mental State Examination', rows: 4 },
        { name: 'mood', type: 'select', label: 'Mood', options: ['Euthymic', 'Depressed', 'Elevated', 'Irritable', 'Anxious', 'Labile'] },
        { name: 'psychosis_symptoms', type: 'multiselect', label: 'Psychotic Symptoms', options: ['Hallucinations', 'Delusions', 'Thought Disorder', 'None'] },
        { name: 'suicide_risk', type: 'select', label: 'Suicide Risk', options: ['Low', 'Moderate', 'High', 'Imminent'] },
        { name: 'substance_use', type: 'multiselect', label: 'Substance Use', options: ['Alcohol', 'Cannabis', 'Opioids', 'Stimulants', 'Benzodiazepines', 'None'] }
      ]
    };

    switch (disease) {
      case 'Depression':
        basePsychiatry.fields.push(
          { name: 'phq9_score', type: 'number', label: 'PHQ-9 Score', min: 0, max: 27 },
          { name: 'depression_severity', type: 'select', label: 'Depression Severity', options: ['Mild', 'Moderate', 'Moderately Severe', 'Severe'] },
          { name: 'antidepressant', type: 'text', label: 'Antidepressant Medication' }
        );
        break;
      case 'Anxiety Disorders':
        basePsychiatry.fields.push(
          { name: 'gad7_score', type: 'number', label: 'GAD-7 Score', min: 0, max: 21 },
          { name: 'anxiety_type', type: 'select', label: 'Anxiety Type', options: ['Generalized Anxiety', 'Panic Disorder', 'Social Anxiety', 'Phobias', 'OCD'] },
          { name: 'panic_attacks', type: 'checkbox', label: 'Panic Attacks Present' }
        );
        break;
    }

    return basePsychiatry;
  };

  const getOrthopedicsSection = (disease) => {
    return {
      title: 'Orthopedic Assessment',
      fields: [
        { name: 'affected_joint', type: 'multiselect', label: 'Affected Joints', options: ['Knee', 'Hip', 'Shoulder', 'Ankle', 'Spine', 'Wrist', 'Elbow', 'Other'] },
        { name: 'pain_scale', type: 'number', label: 'Pain Scale (0-10)', min: 0, max: 10 },
        { name: 'range_of_motion', type: 'textarea', label: 'Range of Motion Assessment', rows: 2 },
        { name: 'functional_assessment', type: 'textarea', label: 'Functional Assessment', rows: 2 },
        { name: 'imaging_results', type: 'textarea', label: 'Imaging Results (X-ray, MRI, CT)', rows: 3 },
        { name: 'surgical_intervention', type: 'checkbox', label: 'Surgical Intervention Required' },
        { name: 'physiotherapy', type: 'checkbox', label: 'Physiotherapy Recommended' }
      ]
    };
  };

  const getPediatricsSection = (disease) => {
    return {
      title: 'Pediatric Assessment',
      fields: [
        { name: 'birth_history', type: 'textarea', label: 'Birth History', rows: 2 },
        { name: 'developmental_milestones', type: 'textarea', label: 'Developmental Milestones', rows: 2 },
        { name: 'growth_parameters', type: 'text', label: 'Growth Parameters (Height, Weight, Head Circumference)' },
        { name: 'immunization_status', type: 'select', label: 'Immunization Status', options: ['Up to date', 'Behind schedule', 'Refused', 'Unknown'] },
        { name: 'school_performance', type: 'text', label: 'School Performance' },
        { name: 'behavioral_concerns', type: 'textarea', label: 'Behavioral Concerns', rows: 2 },
        { name: 'parent_concerns', type: 'textarea', label: 'Parental Concerns', rows: 2 }
      ]
    };
  };

  const getGeriatricsSection = (disease) => {
    return {
      title: 'Geriatric Assessment',
      fields: [
        { name: 'functional_status', type: 'select', label: 'Functional Status', options: ['Independent', 'Partially Dependent', 'Fully Dependent'] },
        { name: 'cognitive_assessment', type: 'select', label: 'Cognitive Assessment', options: ['Normal', 'Mild Cognitive Impairment', 'Moderate Impairment', 'Severe Impairment'] },
        { name: 'fall_risk', type: 'select', label: 'Fall Risk', options: ['Low', 'Moderate', 'High'] },
        { name: 'polypharmacy', type: 'checkbox', label: 'Polypharmacy (≥5 medications)' },
        { name: 'social_support', type: 'select', label: 'Social Support', options: ['Good', 'Adequate', 'Poor', 'Isolated'] },
        { name: 'adl_score', type: 'number', label: 'ADL Score', min: 0, max: 6 },
        { name: 'iadl_score', type: 'number', label: 'IADL Score', min: 0, max: 8 }
      ]
    };
  };

  const getGeneralSection = () => {
    return {
      title: 'Clinical Assessment',
      fields: [
        { name: 'physical_examination', type: 'textarea', label: 'Physical Examination', rows: 4 },
        { name: 'diagnostic_tests', type: 'textarea', label: 'Diagnostic Tests', rows: 3 },
        { name: 'clinical_impression', type: 'textarea', label: 'Clinical Impression', rows: 2 },
        { name: 'differential_diagnosis', type: 'textarea', label: 'Differential Diagnosis', rows: 2 }
      ]
    };
  };

  useEffect(() => {
    fetchTemplates();
  }, [selectedSpecialty]);

  useEffect(() => {
    if (!loading) {
      fetchTemplates();
    }
  }, [showInactive]);

  useEffect(() => {
    if (user?.id) {
      fetchTemplates();
      fetchAvailableSpecialties();
    }
  }, [user, showInactive]);

  const fetchTemplates = async () => {
    try {
      const params = {
        ...(selectedSpecialty && selectedSpecialty !== 'All Specialties' ? { specialty: selectedSpecialty } : {}),
        include_inactive: showInactive
      };
      
      const response = await axios.get(`${API}/templates?user_id=${user.id}`, { params });
      
      // Use only user templates (no predefined templates)
      let allTemplates = response.data;
      
      // Filter by specialty if selected
      if (selectedSpecialty && selectedSpecialty !== 'All Specialties') {
        allTemplates = allTemplates.filter(template => template.specialty === selectedSpecialty);
      }
      
      setTemplates(allTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Set empty templates on error
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSpecialties = async () => {
    try {
      const response = await axios.get(`${API}/templates/specialties`);
      setAvailableSpecialties(response.data.specialties || []);
      setSpecialtyDetails(response.data.specialty_details || {});
    } catch (error) {
      console.error('Error fetching specialties:', error);
    }
  };

  const generateSmartTemplate = async () => {
    if (!formData.specialty || !formData.name) {
      alert('Please provide template name and select a specialty');
      return;
    }

    setSmartTemplateLoading(true);
    try {
      const response = await axios.post(`${API}/templates/generate-smart-template`, {
        name: formData.name,
        specialty: formData.specialty,
        disease: formData.disease || 'General'
      }, {
        params: { user_id: user.id }
      });

      setSmartTemplatePreview(response.data.template);
      setFormData(prev => ({
        ...prev,
        template_data: response.data.template.template_data
      }));
      
      console.log('Smart template generated:', response.data);
      
      const stats = response.data.stats;
      const generationMethod = response.data.template.generation_metadata.generation_method;
      
      let generationMessage = '';
      if (generationMethod === 'predefined') {
        generationMessage = 'Enhanced predefined template with medical expertise';
      } else if (generationMethod === 'ai_dynamic') {
        generationMessage = 'AI-powered dynamic template with specialty-specific medical knowledge';
      } else if (generationMethod === 'fallback') {
        generationMessage = 'Basic template structure (AI generation temporarily unavailable)';
      } else {
        generationMessage = 'Smart template with medical optimization';
      }
      
      alert(`🎉 Smart template generated successfully! 

🤖 Generation Method: ${generationMessage}

📊 Template Statistics:
• ${stats.total_fields} total fields across ${stats.total_sections} sections
• ${stats.dropdown_fields} dropdown fields with medical values
• ${stats.text_fields || 0} text/textarea fields  
• ${stats.numeric_fields || 0} numeric fields

✨ This template is specifically optimized for ${formData.specialty}${formData.disease ? ` - ${formData.disease}` : ''} with medically accurate terminology and dropdown options.

The AI-powered system ensures data consistency and reduces manual entry errors!`);
      
      
    } catch (error) {
      console.error('Error generating smart template:', error);
      alert('Error generating smart template: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSmartTemplateLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    
    // Use the template data that's already in formData.template_data
    // This will contain either AI-generated data (from smart generation) or default data (from manual entry)
    const templateData = {
      ...formData,
      template_data: formData.template_data
    };

    try {
      await axios.post(`${API}/templates?user_id=${user.id}`, templateData);
      setShowCreateModal(false);
      resetForm();
      fetchTemplates();
      alert('Template created successfully!');
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Error creating template. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      specialty: '',
      disease: '',
      template_data: { sections: [] }
    });
    setCurrentStep(1);
    setUseManualEntry(false);
    setSmartTemplatePreview(null);
    setSmartTemplateLoading(false);
  };

  const handleTemplateAction = async (template, action) => {
    const actionMessages = {
      deactivate: 'Are you sure you want to deactivate this Form Template? It will be hidden from the main list but existing MRS using it will continue to work.',
      reactivate: 'Are you sure you want to reactivate this Form Template?',
      delete: 'Are you sure you want to permanently delete this Form Template? This action cannot be undone. Make sure no MRS is using this template.'
    };

    setConfirmAction({
      type: action,
      template: template,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Template`,
      message: actionMessages[action],
      confirmText: action === 'delete' ? 'Delete' : action.charAt(0).toUpperCase() + action.slice(1),
      confirmClass: action === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-sky-600 hover:bg-sky-700'
    });
    setShowConfirmDialog(true);
  };

  const executeAction = async () => {
    if (!confirmAction) return;

    setActionLoading(true);
    try {
      const { type, template } = confirmAction;
      let endpoint;
      let method;

      switch (type) {
        case 'deactivate':
          endpoint = `${API}/templates/${template.id}/deactivate?user_id=${user.id}`;
          method = 'PUT';
          break;
        case 'reactivate':
          endpoint = `${API}/templates/${template.id}/reactivate?user_id=${user.id}`;
          method = 'PUT';
          break;
        case 'delete':
          endpoint = `${API}/templates/${template.id}?user_id=${user.id}`;
          method = 'DELETE';
          break;
        default:
          throw new Error('Invalid action');
      }

      if (method === 'PUT') {
        await axios.put(endpoint);
      } else {
        await axios.delete(endpoint);
      }

      // Refresh the list
      fetchTemplates();
      
      console.log(`Template ${type}d successfully`);
      
    } catch (error) {
      console.error(`Error ${confirmAction.type}ing template:`, error);
      alert(error.response?.data?.detail || `Failed to ${confirmAction.type} template`);
    } finally {
      setActionLoading(false);
      setShowConfirmDialog(false);
      setConfirmAction(null);
    }
  };

  const canManageTemplate = (template) => {
    // Predefined templates cannot be managed
    if (!template.id || template.id.includes('predefined')) return false;
    return template.created_by === user.id || ['admin', 'hod'].includes(user.role);
  };

  // Field management functions for preview mode
  const addFieldToSection = (sectionIndex, field) => {
    if (!previewTemplate) return;
    
    const fieldId = field.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    
    // Prevent adding Patient ID fields as they're managed at MRS level
    if (fieldId === 'patient_id' || fieldId === 'id' || fieldId === '_id') {
      alert('Patient ID fields are not allowed in templates as they are managed at the Medical Record Set level.');
      return;
    }
    
    const updatedTemplate = { ...previewTemplate };
    const fieldWithId = {
      ...field,
      id: fieldId
    };
    
    updatedTemplate.template_data.sections[sectionIndex].fields.push(fieldWithId);
    setPreviewTemplate(updatedTemplate);
  };

  const removeFieldFromSection = (sectionIndex, fieldIndex) => {
    if (!previewTemplate) return;
    
    const updatedTemplate = { ...previewTemplate };
    updatedTemplate.template_data.sections[sectionIndex].fields.splice(fieldIndex, 1);
    setPreviewTemplate(updatedTemplate);
  };

  const updateField = (sectionIndex, fieldIndex, updatedField) => {
    if (!previewTemplate) return;
    
    const updatedTemplate = { ...previewTemplate };
    const fieldWithId = {
      ...updatedField,
      id: updatedField.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
    };
    
    updatedTemplate.template_data.sections[sectionIndex].fields[fieldIndex] = fieldWithId;
    setPreviewTemplate(updatedTemplate);
  };

  const saveTemplateChanges = async () => {
    if (!previewTemplate) return;
    
    try {
      setActionLoading(true);
      
      // Filter out any patient_id fields that might have been added
      const cleanedTemplate = {
        ...previewTemplate,
        template_data: {
          ...previewTemplate.template_data,
          sections: previewTemplate.template_data.sections.map(section => ({
            ...section,
            fields: section.fields.filter(field => 
              field.id !== 'patient_id' && field.id !== 'id' && field.id !== '_id'
            )
          }))
        }
      };
      
      await axios.put(`${API}/templates/${previewTemplate.id}?user_id=${user.id}`, {
        template_data: cleanedTemplate.template_data
      });
      
      // Update the preview template with cleaned version
      setPreviewTemplate(cleanedTemplate);
      
      // Refresh templates list
      fetchTemplates();
      alert('Template updated successfully!');
    } catch (error) {
      console.error('Error updating template:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Error updating template: ${errorMessage}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddField = (sectionIndex) => {
    setNewField({
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: []
    });
    setShowAddFieldModal({ sectionIndex });
  };

  const submitNewField = () => {
    if (!newField.label.trim()) {
      alert('Field label is required');
      return;
    }
    
    addFieldToSection(showAddFieldModal.sectionIndex, newField);
    setShowAddFieldModal(false);
    setNewField({
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: []
    });
  };

  const handleNext = () => {
    if (currentStep === 3) {
      // Use AI-generated template if available, otherwise generate default template
      if (generationMethod === 'smart' && smartTemplatePreview) {
        // AI template already generated and stored in smartTemplatePreview
        console.log('Using AI-generated template for preview');
        setFormData(prev => ({
          ...prev,
          template_data: smartTemplatePreview.template_data
        }));
      } else {
        // Generate the default form based on specialty and disease for manual entry
        console.log('Generating default template for manual entry');
        const generatedTemplate = generateFormTemplate(formData.specialty, formData.disease);
        setFormData(prev => ({
          ...prev,
          template_data: generatedTemplate
        }));
      }
    }
    setCurrentStep(prev => prev + 1);
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };

  const renderFieldPreview = (field) => {
    const baseClasses = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm";
    
    switch (field.type) {
      case 'text':
        return (
          <input 
            type="text" 
            className={baseClasses}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            disabled
            readOnly
          />
        );
      case 'number':
        return (
          <input 
            type="number" 
            className={baseClasses}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            min={field.min}
            max={field.max}
            step={field.step}
            disabled
            readOnly
          />
        );
      case 'textarea':
        return (
          <textarea 
            className={baseClasses}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            rows={field.rows || 3}
            disabled
            readOnly
          />
        );
      case 'select':
        return (
          <select className={baseClasses} disabled>
            <option value="">Select {field.label.toLowerCase()}</option>
            {field.options && field.options.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'multiselect':
        return (
          <div className="mt-1">
            {field.options && field.options.map((option, index) => (
              <label key={index} className="flex items-center mt-2">
                <input type="checkbox" className="mr-2" disabled />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <label className="flex items-center mt-1">
            <input type="checkbox" className="mr-2" disabled />
            <span className="text-sm">{field.label}</span>
          </label>
        );
      case 'datetime-local':
        return (
          <input 
            type="datetime-local" 
            className={baseClasses}
            disabled
            readOnly
          />
        );
      default:
        return (
          <input 
            type="text" 
            className={baseClasses}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            disabled
            readOnly
          />
        );
    }
  };

  const getSpecialtyColor = (specialty) => {
    const colors = {
      'Cardiology': 'bg-red-100 text-red-800 border-red-200',
      'Endocrinology': 'bg-green-100 text-green-800 border-green-200',
      'Neurology': 'bg-sky-100 text-sky-800 border-sky-200',
      'Oncology': 'bg-purple-100 text-purple-800 border-purple-200',
      'Psychiatry': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'Orthopedics': 'bg-orange-100 text-orange-800 border-orange-200',
      'Pediatrics': 'bg-pink-100 text-pink-800 border-pink-200',
      'Geriatrics': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors[specialty] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleUseTemplate = (template) => {
    console.log('Using template:', template);
    alert(`Template "${template.name}" selected! This would typically open a new patient form with the template fields.`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Form Templates</h1>
          <p className="text-gray-600 mt-2">AI-generated specialty forms for structured data entry</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          <span>Create Template</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Filter by Specialty:</label>
          <select
            value={selectedSpecialty}
            onChange={(e) => setSelectedSpecialty(e.target.value)}
            className="form-input py-2 px-3 w-64"
          >
            {specialties.map(specialty => (
              <option key={specialty} value={specialty}>{specialty}</option>
            ))}
          </select>
        </div>
        
        {/* Show Inactive Toggle */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="showInactiveTemplates"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
          />
          <label htmlFor="showInactiveTemplates" className="text-sm text-gray-700">
            Show inactive templates
          </label>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template.id} className={`medical-card hover:shadow-lg transition-all duration-200 ${
            template.is_active === false ? 'opacity-60 border-gray-300' : ''
          }`}>
            <div className="medical-card-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  {template.is_active === false && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Inactive
                    </span>
                  )}
                </div>
                
                {/* Management Dropdown */}
                <div className="flex items-center space-x-2">
                  {template.created_by === 'system' && (
                    <span className="bg-sky-100 text-sky-800 text-xs px-2 py-1 rounded-full">
                      AI Generated
                    </span>
                  )}
                  
                  {canManageTemplate(template) && (
                    <div className="relative group">
                      <button className="text-gray-400 hover:text-gray-600 p-1">
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                        </svg>
                      </button>
                      
                      {/* Dropdown Menu */}
                      <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                        <div className="py-1" role="menu">
                          {template.is_active !== false ? (
                            <button
                              onClick={() => handleTemplateAction(template, 'deactivate')}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                              </svg>
                              Deactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleTemplateAction(template, 'reactivate')}
                              className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                            >
                              <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Reactivate
                            </button>
                          )}
                          
                          <div className="border-t border-gray-100 my-1"></div>
                          
                          <button
                            onClick={() => handleTemplateAction(template, 'delete')}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                          >
                            <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-2 flex items-center space-x-2">
                <span className={`specialty-badge border ${getSpecialtyColor(template.specialty)}`}>
                  {template.specialty}
                </span>
                {template.disease && (
                  <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                    {template.disease}
                  </span>
                )}
              </div>
            </div>
            
            <div className="medical-card-content">
              {template.description && (
                <p className="text-gray-600 text-sm mb-4">{template.description}</p>
              )}
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  <span>{template.template_data.sections?.length || 0} sections</span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                  </svg>
                  <span>
                    {template.template_data.sections?.reduce((total, section) => 
                      total + (section.fields?.length || 0), 0) || 0} fields
                  </span>
                </div>
                
                <div className="flex items-center text-sm text-gray-500">
                  <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  <span>Created {formatDate(template.created_at)}</span>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleUseTemplate(template)}
                  className="flex-1 btn-primary text-sm py-2"
                >
                  Use Template
                </button>
                <button 
                  onClick={() => handlePreview(template)}
                  className="flex-1 btn-secondary text-sm py-2"
                >
                  Preview
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {templates.length === 0 && (
        <div className="text-center py-12">
          <svg className="h-24 w-24 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-xl font-medium text-gray-900 mb-2">No Templates Found</h3>
          <p className="text-gray-600 mb-6">
            {selectedSpecialty && selectedSpecialty !== 'All Specialties' 
              ? `No templates found for ${selectedSpecialty}. Try selecting a different specialty or create a new template.`
              : 'Create your first form template to streamline data entry.'
            }
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            Create New Template
          </button>
        </div>
      )}

      {/* Enhanced Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Create AI-Generated Form Template</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              
              {/* Progress Steps */}
              <div className="flex items-center mt-4">
                {[1, 2, 3, 4].map((step) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep >= step ? 'bg-sky-600 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>
                      {step}
                    </div>
                    {step < 4 && (
                      <div className={`w-16 h-1 ${currentStep > step ? 'bg-sky-600' : 'bg-gray-300'}`}></div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="flex space-x-16 mt-2 text-xs text-gray-500">
                <span>Name</span>
                <span>Specialty</span>
                <span>Disease</span>
                <span>Generated Form</span>
              </div>
            </div>
            
            <form onSubmit={handleCreateTemplate} className="p-6">
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Step 1: Template Name & Generation Method</h3>
                  
                  <div>
                    <label className="form-label">Template Name *</label>
                    <input
                      type="text"
                      required
                      className="form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Cardiology Assessment Form"
                    />
                  </div>

                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-medium text-gray-900 mb-3">Template Generation Method</h4>
                    <div className="space-y-3">
                      <label className="flex items-start">
                        <input
                          type="radio"
                          name="generationMethod"
                          value="smart"
                          checked={generationMethod === 'smart'}
                          onChange={(e) => setGenerationMethod(e.target.value)}
                          className="mt-1 mr-3"
                        />
                        <div>
                          <span className="font-medium text-green-700">✨ Smart Template Generation (Recommended)</span>
                          <p className="text-sm text-gray-600 mt-1">
                            Automatically generates comprehensive form fields with medical dropdown options based on your specialty.
                            Creates standardized fields with predefined values for better data consistency.
                          </p>
                          <div className="text-xs text-green-600 mt-1">
                            • Pre-configured medical dropdown options • Specialty-specific field types • Improved data quality
                          </div>
                        </div>
                      </label>
                      
                      <label className="flex items-start">
                        <input
                          type="radio"
                          name="generationMethod"
                          value="manual"
                          checked={generationMethod === 'manual'}
                          onChange={(e) => setGenerationMethod(e.target.value)}
                          className="mt-1 mr-3"
                        />
                        <div>
                          <span className="font-medium text-gray-700">Manual Template Creation</span>
                          <p className="text-sm text-gray-600 mt-1">
                            Create template fields manually with custom field types and options.
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {generationMethod === 'smart' && (
                    <div className="bg-sky-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-sky-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <h5 className="font-medium text-sky-900">Smart Template Benefits</h5>
                          <p className="text-sm text-sky-700 mt-1">
                            Available for: {availableSpecialties.join(', ') || 'Loading...'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Step 2: Medical Specialty</h3>
                  
                  {generationMethod === 'smart' ? (
                    <div>
                      <div className="mb-4">
                        <div className="flex items-center space-x-4 mb-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="specialtyEntryMode"
                              checked={!useManualEntry}
                              onChange={() => setUseManualEntry(false)}
                              className="mr-2"
                            />
                            <span className="text-sm">Select from common specialties</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="specialtyEntryMode"
                              checked={useManualEntry}
                              onChange={() => setUseManualEntry(true)}
                              className="mr-2"
                            />
                            <span className="text-sm">Enter any specialty</span>
                          </label>
                        </div>
                      </div>

                      {!useManualEntry ? (
                        <div>
                          <label className="form-label">Select Medical Specialty *</label>
                          <select
                            required
                            className="form-input"
                            value={formData.specialty}
                            onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                          >
                            <option value="">Choose a specialty...</option>
                            {availableSpecialties.map((specialty) => (
                              <option key={specialty} value={specialty}>
                                {specialty} {specialtyDetails[specialty] ? '(Enhanced with AI)' : '(AI-powered)'}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <div>
                          <label className="form-label">Enter Any Medical Specialty *</label>
                          <input
                            type="text"
                            required
                            className="form-input"
                            value={formData.specialty}
                            onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                            placeholder="e.g., Interventional Cardiology, Pediatric Surgery, Reproductive Endocrinology"
                          />
                          <p className="text-sm text-gray-600 mt-1">
                            Enter any medical specialty - AI will generate appropriate fields and dropdown options
                          </p>
                        </div>
                      )}
                      
                      {formData.specialty && (
                        <div className="mt-3 p-3 bg-sky-50 rounded-lg">
                          <h4 className="font-medium text-sky-900">🤖 AI-Powered Smart Template for {formData.specialty}</h4>
                          {specialtyDetails[formData.specialty] ? (
                            <div>
                              <div className="text-sm text-sky-700 mt-1">
                                Enhanced predefined template with sections: {specialtyDetails[formData.specialty].sections?.join(', ')}
                              </div>
                              <div className="text-xs text-sky-600 mt-1">
                                {specialtyDetails[formData.specialty].total_fields} optimized fields + AI enhancements
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="text-sm text-sky-700 mt-1">
                                Will generate specialty-specific sections: Demographics, Medical History, Therapy Details, Outcomes
                              </div>
                              <div className="text-xs text-sky-600 mt-1">
                                AI will create medically accurate fields and dropdown options for {formData.specialty}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mb-4">
                      <div className="flex items-center space-x-4 mb-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="entryMode"
                            checked={!useManualEntry}
                            onChange={() => setUseManualEntry(false)}
                            className="mr-2"
                          />
                          <span className="text-sm">Select from predefined specialties</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="entryMode"
                            checked={useManualEntry}
                            onChange={() => setUseManualEntry(true)}
                            className="mr-2"
                          />
                          <span className="text-sm">Enter custom specialty</span>
                        </label>
                      </div>

                      {!useManualEntry ? (
                        <div>
                          <label className="form-label">Select Medical Specialty *</label>
                          <select
                            required
                            className="form-input"
                            value={formData.specialty}
                            onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                          >
                            <option value="">Choose a specialty...</option>
                            <option value="Cardiology">Cardiology</option>
                            <option value="Neurology">Neurology</option>
                            <option value="Orthopedics">Orthopedics</option>
                            <option value="Pediatrics">Pediatrics</option>
                            <option value="Psychiatry">Psychiatry</option>
                            <option value="Dermatology">Dermatology</option>
                            <option value="Endocrinology">Endocrinology</option>
                            <option value="Gastroenterology">Gastroenterology</option>
                            <option value="Pulmonology">Pulmonology</option>
                            <option value="Oncology">Oncology</option>
                            <option value="Radiology">Radiology</option>
                            <option value="Anesthesiology">Anesthesiology</option>
                            <option value="Emergency Medicine">Emergency Medicine</option>
                            <option value="Internal Medicine">Internal Medicine</option>
                          </select>
                        </div>
                      ) : (
                        <div>
                          <label className="form-label">Enter Custom Specialty *</label>
                          <input
                            type="text"
                            required
                            className="form-input"
                            value={formData.specialty}
                            onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                            placeholder="e.g., Interventional Cardiology"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Step 3: Disease/Condition & Template Generation</h3>
                  
                  {generationMethod === 'smart' ? (
                    <div className="space-y-4">
                      <div>
                        <label className="form-label">Disease/Condition (Optional)</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.disease}
                          onChange={(e) => setFormData({...formData, disease: e.target.value})}
                          placeholder="e.g., Coronary Artery Disease, Heart Failure"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Helps customize the template further (optional)
                        </p>
                      </div>
                      
                      <div className="border rounded-lg p-4 bg-sky-50">
                        <h4 className="font-medium text-sky-900 flex items-center">
                          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          Generate Smart Template
                        </h4>
                        <p className="text-sm text-sky-700 mt-2">
                          Ready to generate a comprehensive form template with predefined medical dropdown options
                          for {formData.specialty}{formData.disease ? ` - ${formData.disease}` : ''}.
                        </p>
                        
                        <button
                          type="button"
                          onClick={generateSmartTemplate}
                          disabled={smartTemplateLoading || !formData.specialty}
                          className="mt-3 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                        >
                          {smartTemplateLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Generating...
                            </>
                          ) : (
                            <>
                              <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414-1.414L9 5.586 7.707 4.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L9 5.586z" clipRule="evenodd"/>
                              </svg>
                              Generate Smart Template
                            </>
                          )}
                        </button>
                        
                        {smartTemplatePreview && (
                          <div className="mt-3 p-3 bg-green-100 rounded-md">
                            <p className="text-sm text-green-800">
                              ✅ Smart template generated successfully! Proceed to Step 4 to review and customize.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      {!useManualEntry && formData.specialty && diseasesBySpecialty[formData.specialty] ? (
                        <div>
                          <label className="form-label">Select Disease/Condition *</label>
                          <select
                            required
                            className="form-input"
                            value={formData.disease}
                            onChange={(e) => setFormData({...formData, disease: e.target.value})}
                          >
                            <option value="">Choose a disease/condition...</option>
                            {diseasesBySpecialty[formData.specialty].map(disease => (
                              <option key={disease} value={disease}>{disease}</option>
                            ))}
                          </select>
                          <div className="mt-2">
                            <label className="flex items-center text-sm">
                              <input
                                type="checkbox"
                                checked={useManualEntry}
                                onChange={(e) => setUseManualEntry(e.target.checked)}
                                className="mr-2"
                              />
                              <span>Enter custom disease/condition instead</span>
                            </label>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className="form-label">Enter Disease/Condition *</label>
                          <input
                            type="text"
                            required
                            className="form-input"
                            value={formData.disease}
                            onChange={(e) => setFormData({...formData, disease: e.target.value})}
                            placeholder="e.g., Chronic Kidney Disease, Fibromyalgia, Skin Cancer"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Enter the specific disease or condition that this form will be used for
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {currentStep === 4 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Step 4: Generated Form Preview</h3>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <svg className="h-6 w-6 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <h4 className="font-semibold text-green-900">AI Form Generated Successfully!</h4>
                        <p className="text-sm text-green-800 mt-1">
                          Based on <strong>{formData.specialty}</strong> specialty and <strong>{formData.disease}</strong> condition, 
                          we've generated a comprehensive form with {formData.template_data.sections?.length || 0} sections and{' '}
                          {formData.template_data.sections?.reduce((total, section) => total + (section.fields?.length || 0), 0) || 0} fields.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
                    {formData.template_data.sections?.map((section, sectionIndex) => (
                      <div key={sectionIndex} className="mb-6">
                        <h4 className="text-md font-medium text-gray-900 mb-3 border-b border-gray-200 pb-2">
                          {section.title}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {section.fields?.map((field, fieldIndex) => (
                            <div key={fieldIndex} className="space-y-1">
                              <label className="block text-sm font-medium text-gray-700">
                                {field.label}
                                {field.required && <span className="text-red-500 ml-1">*</span>}
                              </label>
                              {renderFieldPreview(field)}
                              {field.options && field.type === 'select' && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Options: {field.options.join(', ')}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => currentStep > 1 ? setCurrentStep(prev => prev - 1) : (setShowCreateModal(false), resetForm())}
                  className="btn-secondary"
                >
                  {currentStep > 1 ? 'Previous' : 'Cancel'}
                </button>
                
                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={
                      (currentStep === 1 && !formData.name) ||
                      (currentStep === 2 && !formData.specialty) ||
                      (currentStep === 3 && generationMethod === 'smart' && !smartTemplatePreview) ||
                      (currentStep === 3 && generationMethod === 'manual' && !formData.disease)
                    }
                    className="btn-primary disabled:opacity-50"
                  >
                    {currentStep === 3 && generationMethod === 'smart' && !smartTemplatePreview ? 'Generate Template First' : 'Next'}
                  </button>
                ) : (
                  <button type="submit" className="btn-primary">
                    Create Template
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enhanced Preview Modal */}
      {showPreviewModal && previewTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{previewTemplate.name}</h2>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`specialty-badge border ${getSpecialtyColor(previewTemplate.specialty)}`}>
                      {previewTemplate.specialty}
                    </span>
                    {previewTemplate.disease && (
                      <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                        {previewTemplate.disease}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-8">
                {previewTemplate.template_data.sections?.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                        {sectionIndex + 1}
                      </div>
                      {section.title}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {section.fields?.map((field, fieldIndex) => (
                        <div key={fieldIndex} className="bg-white p-4 rounded-lg border border-gray-200 relative group">
                          {/* Field actions */}
                          {canManageTemplate(previewTemplate) && (
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => setEditingField({ sectionIndex, fieldIndex, field: {...field} })}
                                  className="text-sky-600 hover:text-sky-800 p-1"
                                  title="Edit field"
                                >
                                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => removeFieldFromSection(sectionIndex, fieldIndex)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Remove field"
                                >
                                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          )}
                          
                          <div className="mb-2">
                            <label className="block text-sm font-medium text-gray-900">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <div className="text-xs text-gray-500 mt-1">
                              Type: <span className="font-medium">{field.type}</span>
                              {field.required && <span className="text-red-600 ml-2">Required</span>}
                            </div>
                          </div>
                          
                          {renderFieldPreview(field)}
                          
                          {/* Show options for select fields */}
                          {field.options && (field.type === 'select' || field.type === 'multiselect') && (
                            <div className="mt-3 p-2 bg-sky-50 rounded text-xs">
                              <div className="font-medium text-sky-900 mb-1">Available Options:</div>
                              <div className="text-sky-800">
                                {field.options.map((option, optionIndex) => (
                                  <span key={optionIndex} className="inline-block bg-sky-100 text-sky-800 px-2 py-1 rounded mr-1 mb-1">
                                    {option}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Show validation rules */}
                          {(field.min !== undefined || field.max !== undefined || field.step !== undefined) && (
                            <div className="mt-2 text-xs text-gray-500">
                              {field.min !== undefined && <span>Min: {field.min}</span>}
                              {field.max !== undefined && <span className="ml-2">Max: {field.max}</span>}
                              {field.step !== undefined && <span className="ml-2">Step: {field.step}</span>}
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Add field button */}
                      {canManageTemplate(previewTemplate) && (
                        <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                          <button
                            onClick={() => handleAddField(sectionIndex)}
                            className="flex items-center space-x-2 text-sky-600 hover:text-sky-800"
                          >
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            <span>Add Field</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 flex justify-end space-x-3">
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
                {canManageTemplate(previewTemplate) && (
                  <button
                    onClick={saveTemplateChanges}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Save Changes
                  </button>
                )}
                <button
                  onClick={() => {
                    handleUseTemplate(previewTemplate);
                    setShowPreviewModal(false);
                  }}
                  className="btn-primary"
                >
                  Use This Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && confirmAction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{confirmAction.title}</h2>
            </div>
            
            <div className="p-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  {confirmAction.type === 'delete' ? (
                    <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6 text-sky-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{confirmAction.message}</p>
                  
                  <div className="mt-3 p-3 bg-gray-50 rounded">
                    <p className="text-sm font-medium text-gray-900">Template: {confirmAction.template.name}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {confirmAction.template.specialty} • {confirmAction.template.template_data?.sections?.length || 0} sections
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setConfirmAction(null);
                }}
                disabled={actionLoading}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={executeAction}
                disabled={actionLoading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${confirmAction.confirmClass}`}
              >
                {actionLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  confirmAction.confirmText
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Field Modal */}
      {showAddFieldModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Add New Field</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Field Label *</label>
                <input
                  type="text"
                  value={newField.label}
                  onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Blood Pressure"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                <select
                  value={newField.type}
                  onChange={(e) => setNewField({ ...newField, type: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="email">Email</option>
                  <option value="date">Date</option>
                  <option value="textarea">Textarea</option>
                  <option value="select">Dropdown (Select)</option>
                  <option value="checkbox">Checkbox</option>
                  <option value="radio">Radio Buttons</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder</label>
                <input
                  type="text"
                  value={newField.placeholder}
                  onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Enter blood pressure reading"
                />
              </div>
              
              {(newField.type === 'select' || newField.type === 'radio') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Options (comma-separated)
                    <span className="text-xs text-gray-500 ml-2">
                      E.g., Option 1, Option 2, Option 3
                    </span>
                  </label>
                  <textarea
                    rows={3}
                    value={Array.isArray(newField.options) ? newField.options.join(', ') : ''}
                    onChange={(e) => {
                      const optionsText = e.target.value;
                      const optionsArray = optionsText
                        .split(',')
                        .map(opt => opt.trim())
                        .filter(opt => opt.length > 0);
                      
                      setNewField({ 
                        ...newField, 
                        options: optionsArray
                      });
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Option 1, Option 2, Option 3"
                  />
                  <div className="text-xs text-gray-600 mt-1">
                    Current options: {Array.isArray(newField.options) ? newField.options.length : 0}
                    {newField.options && newField.options.length > 0 && (
                      <span className="ml-2">
                        [{newField.options.slice(0, 3).join(', ')}{newField.options.length > 3 ? '...' : ''}]
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="required"
                  checked={newField.required}
                  onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                  className="h-4 w-4 text-sky-600 border-gray-300 rounded"
                />
                <label htmlFor="required" className="ml-2 text-sm text-gray-700">
                  Required field
                </label>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddFieldModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={submitNewField}
                className="btn-primary"
              >
                Add Field
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Field Modal */}
      {editingField && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Edit Field</h3>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Field Label</label>
                <input
                  type="text"
                  value={editingField.field.label}
                  onChange={(e) => setEditingField({
                    ...editingField,
                    field: { ...editingField.field, label: e.target.value }
                  })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Blood Pressure"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                <select
                  value={editingField.field.type}
                  onChange={(e) => setEditingField({
                    ...editingField,
                    field: { ...editingField.field, type: e.target.value }
                  })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="email">Email</option>
                  <option value="date">Date</option>
                  <option value="textarea">Textarea</option>
                  <option value="select">Dropdown (Select)</option>
                  <option value="checkbox">Checkbox</option>
                  <option value="radio">Radio Buttons</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder</label>
                <input
                  type="text"
                  value={editingField.field.placeholder || ''}
                  onChange={(e) => setEditingField({
                    ...editingField,
                    field: { ...editingField.field, placeholder: e.target.value }
                  })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Enter blood pressure reading"
                />
              </div>
              
              {(editingField.field.type === 'select' || editingField.field.type === 'radio') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Options (comma-separated)
                    <span className="text-xs text-gray-500 ml-2">
                      E.g., Option 1, Option 2, Option 3
                    </span>
                  </label>
                  <textarea
                    rows={3}
                    value={Array.isArray(editingField.field.options) ? editingField.field.options.join(', ') : ''}
                    onChange={(e) => {
                      const optionsText = e.target.value;
                      const optionsArray = optionsText
                        .split(',')
                        .map(opt => opt.trim())
                        .filter(opt => opt.length > 0);
                      
                      setEditingField({
                        ...editingField,
                        field: { ...editingField.field, options: optionsArray }
                      });
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Option 1, Option 2, Option 3"
                  />
                  <div className="text-xs text-gray-600 mt-1">
                    Current options: {Array.isArray(editingField.field.options) ? editingField.field.options.length : 0}
                    {editingField.field.options && editingField.field.options.length > 0 && (
                      <span className="ml-2">
                        [{editingField.field.options.slice(0, 3).join(', ')}{editingField.field.options.length > 3 ? '...' : ''}]
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="editRequired"
                  checked={editingField.field.required || false}
                  onChange={(e) => setEditingField({
                    ...editingField,
                    field: { ...editingField.field, required: e.target.checked }
                  })}
                  className="h-4 w-4 text-sky-600 border-gray-300 rounded"
                />
                <label htmlFor="editRequired" className="ml-2 text-sm text-gray-700">
                  Required field
                </label>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setEditingField(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!editingField.field.label.trim()) {
                    alert('Field label is required');
                    return;
                  }
                  
                  // Prevent patient_id fields
                  const fieldId = editingField.field.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                  if (fieldId === 'patient_id' || fieldId === 'id' || fieldId === '_id') {
                    alert('Patient ID fields are not allowed in templates as they are managed at the Medical Record Set level.');
                    return;
                  }
                  
                  updateField(editingField.sectionIndex, editingField.fieldIndex, editingField.field);
                  setEditingField(null);
                }}
                className="btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormTemplates;